import React from 'react';
import { Box, FormControl, Select, MenuItem } from '@mui/material';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// What a record stores for "None", as this picker has always handed it over.
const NONE = Object.freeze({ label: 'none', _id: '' });
// The key of a record's own note when it has no id: the spreadsheet importer writes a label alone.
const RECORD_NOTE_KEY = 'record-note';

const isNote = (note) => !!note && typeof note === 'object';
const idOf = (note) => (isNote(note) && note._id ? String(note._id) : '');
const hasLabel = (note) =>
  isNote(note) && typeof note.label === 'string' && note.label.trim() !== '';
// The record's note as a key: its id; else its label, unless that is None's.
const recordKeyOf = (note) =>
  idOf(note) ||
  (hasLabel(note) && note.label !== NONE.label ? RECORD_NOTE_KEY : '');

/**
 * Picks the note a record is associated with (a consent, a referral). The record stores the
 * chosen option, `{ _id, label }`, or `{ label: 'none', _id: '' }` for None, and `handleChange`
 * is handed exactly that as `target.value`.
 *
 * The Select's own value is a key (the note's id), never the object. MUI matches an object value
 * by identity and writes it into the field's input as text, so an absent note (a fresh object on
 * every render) matched nothing and read "[object Object]" (TestForm.associatedConsent.test.jsx).
 * The record's own note, when the options do not hold it (not loaded yet, no longer offered, or
 * imported with a label alone), is offered as the record holds it, so it shows its label and can
 * be kept. Anything in `noteOptions` that is not a note with an id (TestForm's 'loading...'
 * placeholder) is not an option.
 */
export default function AssociatedNote({
  name,
  value,
  handleChange,
  noteOptions,
  readOnly
}) {
  const t = useAutoTranslation();

  const selectedKey = recordKeyOf(value);
  const options = React.useMemo(() => {
    const listed = (Array.isArray(noteOptions) ? noteOptions : [])
      .filter((note) => idOf(note))
      .map((note) => ({ key: idOf(note), label: note.label, note }));
    if (selectedKey && !listed.some((option) => option.key === selectedKey)) {
      listed.push({ key: selectedKey, label: value.label ?? '', note: value });
    }
    return listed;
  }, [noteOptions, selectedKey, value]);

  const onChange = (event) => {
    const key = String(event?.target?.value ?? '');
    const chosen = key && options.find((option) => option.key === key);
    handleChange({
      target: { name, value: chosen ? chosen.note : { ...NONE } }
    });
  };

  return (
    <Box sx={{ display: 'flex', position: 'relative' }}>
      <FormControl fullWidth>
        <Select
          name={name}
          style={{ width: '100%' }}
          value={selectedKey}
          onChange={onChange}
          readOnly={readOnly}
        >
          {options.map((option) => (
            <MenuItem value={option.key} key={option.key}>
              {option.label}
            </MenuItem>
          ))}
          <MenuItem value="">{t('None')}</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
}
